import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { GlassContext, GlassRenderer, type GlassRegionHandle } from 'liquidglass-react';

interface SceneGlassProviderProps {
  children: React.ReactNode;
  device: GPUDevice;
  sceneTexture: GPUTexture | null;
}

/**
 * Custom GlassProvider that uses GlassRenderer directly with an external
 * WebGPU device and scene texture from the C++ renderer. No WASM engine needed.
 *
 * The C++ renderer renders the 3D scene to an offscreen texture.
 * GlassRenderer reads that texture and applies glass effects (blur, refraction)
 * to registered HTML elements, compositing the result onto a full-viewport canvas.
 */
export function SceneGlassProvider({ children, device, sceneTexture }: SceneGlassProviderProps) {
  const [ready, setReady] = useState(false);
  const rendererRef = useRef<GlassRenderer | null>(null);
  const canvasContextRef = useRef<GPUCanvasContext | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Initialize GlassRenderer with the external device
  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!canvasRef.current || !device) return;

      const format = navigator.gpu.getPreferredCanvasFormat();
      const renderer = new GlassRenderer();
      await renderer.init(device, format);
      if (cancelled) { renderer.destroy(); return; }

      const canvas = canvasRef.current!;
      const context = canvas.getContext('webgpu')!;
      context.configure({ device, format, alphaMode: 'premultiplied' });

      rendererRef.current = renderer;
      canvasContextRef.current = context;
      setReady(true);
    })().catch(err => {
      console.error('SceneGlassProvider: init failed', err);
    });

    return () => {
      cancelled = true;
      const ctx = canvasContextRef.current;
      if (ctx) { try { ctx.unconfigure(); } catch { /* ignore */ } }
      canvasContextRef.current = null;
      const renderer = rendererRef.current;
      if (renderer) { renderer.destroy(); rendererRef.current = null; }
      setReady(false);
    };
  }, [device]);

  // Update scene texture when it changes (e.g., after resize)
  useEffect(() => {
    if (!ready || !sceneTexture || !rendererRef.current) return;
    rendererRef.current.setSceneTexture(sceneTexture);
  }, [ready, sceneTexture]);

  // Render loop — GlassRenderer driven from its own RAF
  useEffect(() => {
    if (!ready) return;
    let rafId: number;
    let lastTime = 0;

    const loop = (now: number) => {
      const dt = lastTime === 0 ? 0 : Math.min((now - lastTime) / 1000, 0.1);
      lastTime = now;

      const renderer = rendererRef.current;
      const ctx = canvasContextRef.current;
      const canvas = canvasRef.current;

      if (renderer && ctx && canvas) {
        renderer.render(ctx, canvas.width, canvas.height, dt, devicePixelRatio);
      }
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafId);
  }, [ready]);

  // ResizeObserver for the glass canvas
  useEffect(() => {
    if (!ready) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.devicePixelContentBoxSize?.[0].inlineSize
          ?? Math.round(entry.contentBoxSize[0].inlineSize * devicePixelRatio);
        const height = entry.devicePixelContentBoxSize?.[0].blockSize
          ?? Math.round(entry.contentBoxSize[0].blockSize * devicePixelRatio);
        const maxDim = 4096;
        const w = Math.max(1, Math.min(width, maxDim));
        const h = Math.max(1, Math.min(height, maxDim));
        if (canvas.width !== w || canvas.height !== h) {
          canvas.width = w;
          canvas.height = h;
        }
      }
    });
    try {
      observer.observe(canvas, { box: 'device-pixel-content-box' as ResizeObserverBoxOptions });
    } catch {
      observer.observe(canvas, { box: 'content-box' });
    }
    return () => observer.disconnect();
  }, [ready]);

  const registerRegion = useCallback((element: HTMLElement): GlassRegionHandle | null => {
    const renderer = rendererRef.current;
    if (!renderer) return null;
    const id = renderer.addRegion(element);
    return {
      id,
      updateRect: () => { /* no-op: DOM position is authoritative */ },
      updateParams: (cr, blur, op, refr) => renderer.setRegionParams(id, cr, blur, op, refr),
      updateTint: (r, g, b) => renderer.setRegionTint(id, r, g, b),
      updateAberration: (v) => renderer.setRegionAberration(id, v),
      updateSpecular: (v) => renderer.setRegionSpecular(id, v),
      updateRim: (v) => renderer.setRegionRim(id, v),
      updateMode: (v) => renderer.setRegionMode(id, v),
      updateMorphSpeed: (speed) => renderer.setRegionMorphSpeed(id, speed),
      updateContrast: (v) => renderer.setRegionContrast(id, v),
      updateSaturation: (v) => renderer.setRegionSaturation(id, v),
      updateBlurRadius: (v) => renderer.setRegionBlurRadius(id, v),
      updateFresnelIOR: (v) => renderer.setRegionFresnelIOR(id, v),
      updateFresnelExponent: (v) => renderer.setRegionFresnelExponent(id, v),
      updateEnvReflectionStrength: (v) => renderer.setRegionEnvReflectionStrength(id, v),
      updateGlareAngle: (v) => renderer.setRegionGlareAngle(id, v),
      remove: () => renderer.removeRegion(id),
    };
  }, [ready]); // eslint-disable-line react-hooks/exhaustive-deps

  const unregisterRegion = useCallback((id: number) => {
    rendererRef.current?.removeRegion(id);
  }, []);

  const contextValue = useMemo(() => ({
    registerRegion,
    unregisterRegion,
    ready,
    preferences: { reducedMotion: false, reducedTransparency: false, darkMode: false },
  }), [registerRegion, unregisterRegion, ready]);

  return (
    <GlassContext value={contextValue}>
      <canvas
        id="gpu-canvas"
        ref={canvasRef}
        style={{
          position: 'fixed',
          inset: 0,
          width: '100%',
          height: '100%',
          zIndex: -1,
          display: 'block',
        }}
      />
      {children}
    </GlassContext>
  );
}
